import React from "react";
import { Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import Spinner from "@/components/Spinner";
import { useUserStore } from "@/hooks/useUserStore";

interface RequireActionProps {
  /** DB action code the user's roles must contain (e.g. PART_TIMELINE_VIEW). */
  code: string;
  children: React.ReactNode;
}

/**
 * Renders its children only when one of the user's roles has the action
 * `code` (`userHasAccessToCode`); anyone else is sent to the 403 page.
 * Defence in depth for routes with `requiredActions` — AppRoutes already
 * redirects via `userHasAccess`, this also stops the page (and its API
 * calls) from mounting before that asynchronous check settles.
 */
const RequireAction: React.FC<RequireActionProps> = ({ code, children }) => {
  const { roles, isLoading, userHasAccessToCode } = useUserStore();

  if (isLoading && roles.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <Spinner />
      </Box>
    );
  }

  if (!userHasAccessToCode(code)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};

export default RequireAction;
